<template>
  <popup-modal ref="popup">
    <DialogCloseButton :close-method="closeDialog" />

    <h2 class="font-weight-bold title content">
      {{ $t('UPDATE_STATUS_FORM_MODAL.TITLE') }}
    </h2>

    <div class="my-8 content">
      <p class="sub-title">
        {{ $t('UPDATE_STATUS_FORM_MODAL.MESSAGE') }}
      </p>
    </div>

    <v-form ref="formChangeStatus" @submit.prevent="saveAndClose" v-model="formValid">
      <v-row>
        <v-col cols="8">
          <v-select
            id="select-status"
            v-model="selectedStatus"
            :items="availableStatus"
            :rules="requiredRules"
            :no-data-text="$t('COMMON.NO_DATA')"
            :placeholder="$t('UPDATE_STATUS_FORM_MODAL.PLACEHOLDER')"
            clearable
            hide-details
            item-title="value"
            item-value="key"
            required
          >
            <template v-slot:selection="{ item }">
              <v-chip size="small">
                <span>{{ item.title }}</span>
              </v-chip>
            </template>
            <template v-slot:item="{ item, props }">
              <v-list-item :title="item.value" :value="item.key" v-bind="props">
                <v-tooltip
                  v-if="shouldShowTooltip(item, 'select-status')"
                  activator="parent"
                  location="bottom left"
                >
                  {{ item.value }}
                </v-tooltip>
              </v-list-item>
            </template>
          </v-select>
        </v-col>

        <v-col cols="4" class="d-flex justify-center">
          <div class="d-flex align-center">
            <v-menu :close-on-content-click="false" z-index="3500">
              <template v-slot:activator="{ props }">
                <img
                  v-bind="props"
                  alt="export"
                  class="date-picker"
                  src="@/assets/icons/repair-file-list/date.svg"
                  @click="showValidDate = true"
                />
                <v-text-field
                  v-bind="props"
                  class="ml-3 date-creation"
                  v-model="realUpdatingDate"
                  :value="formatDate()"
                  readonly
                  hide-details
                  @click="showValidDate = true"
                />
                <v-icon
                  icon="mdi-check-circle-outline"
                  color="#767676"
                  class="update-icon"
                  v-if="showValidDate"
                  @click="showValidDate = false"
                />
              </template>
              <v-locale-provider locale="fr">
                <v-date-picker
                  v-model="realUpdatingDate"
                  hide-header
                  color="#559A1B"
                  locale="fr"
                  :max="today"
                  :min="getMinDate()"
                  @focusin="showValidDate = true"
                  border
                />
              </v-locale-provider>
            </v-menu>
          </div>
        </v-col>
      </v-row>

      <!-- RECEIPT_AT_STORE Client Notification-->
      <v-row
        v-if="selectedStatus === RepairFileStatusMacroConstant.RECEIPT_AT_STORE"
        class="receipt-at-store"
      >
        <v-col cols="12">
          <!--Title-->
          <v-row class="title">
            <v-col cols="12">
              {{ $t('UPDATE_STATUS_CONFIRMATION_MODAL.RECEIPT_AT_STORE_TITLE') }}
            </v-col>
          </v-row>

          <v-row justify="space-around">
            <!--Phone notification type-->
            <v-col cols="12" md="6">
              <v-row>
                <v-col cols="12">
                  <div class="phone-notification-title">
                    <v-icon icon="mdi-phone" color="#72ae4b" class="update-icon" />
                    {{ $t('UPDATE_STATUS_CONFIRMATION_MODAL.PHONE_NOTIFICATION.TITLE') }}
                  </div>
                  <v-row>
                    <v-col cols="12">
                      <v-radio-group v-model="clientPhoneNotificationType" hide-details>
                        <v-radio
                          color="#559a1b"
                          :label="$t('UPDATE_STATUS_CONFIRMATION_MODAL.PHONE_NOTIFICATION.CALL')"
                          value="CALL_NOTIFICATION"
                        />

                        <v-radio
                          color="#559a1b"
                          :label="$t('UPDATE_STATUS_CONFIRMATION_MODAL.PHONE_NOTIFICATION.SMS')"
                          value="SMS_NOTIFICATION"
                        />
                      </v-radio-group>
                    </v-col>
                  </v-row>

                  <v-row v-if="clientPhoneNotificationType === 'SMS_NOTIFICATION'">
                    <v-col cols="12">
                      <v-phone-input
                        v-model="smsPhoneNumber"
                        v-model:country="selectedCountry"
                        :includeCountries="allCountries"
                        :rules="requiredRules"
                        :error-messages="error?.smsPhoneNumber"
                      />
                    </v-col>
                    <v-divider vertical color="#72ae4b" class="border-opacity-50" />
                  </v-row>
                </v-col>
              </v-row>
            </v-col>

            <!--Takeover type-->
            <v-col cols="12" md="6">
              <v-row class="email-takeover-part">
                <v-col cols="1" class="custom-divider">
                  <v-divider vertical color="#72ae4b" class="border-opacity-50" />
                </v-col>
                <v-col cols="11" class="custom-takeover-part">
                  <div class="takeover-type-title">
                    <v-icon icon="mdi-email-open-outline" color="#72ae4b" class="update-icon" />
                    {{ $t('UPDATE_STATUS_CONFIRMATION_MODAL.TAKEOVER_TYPE.TITLE') }}
                  </div>
                  <v-row>
                    <v-col cols="12">
                      <v-radio-group v-model="clientTakeoverType" hide-details>
                        <v-radio
                          color="#559a1b"
                          :label="$t('UPDATE_STATUS_CONFIRMATION_MODAL.TAKEOVER_TYPE.PICK_UP')"
                          value="PICK_UP"
                        />
                        <v-radio
                          color="#559a1b"
                          :label="
                            $t('UPDATE_STATUS_CONFIRMATION_MODAL.TAKEOVER_TYPE.SEND_BY_STORE')
                          "
                          value="SEND_BY_STORE"
                        />
                      </v-radio-group>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12">
                      <v-text-field
                        v-model="emailAddress"
                        :rules="[...requiredRules, ...emailRules, ...max255CharRules]"
                        type="email"
                        counter="255"
                        hide-details
                      />
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-col>
      </v-row>

      <v-row>
        <v-col>
          <v-btn
            class="boom-button dialog-button"
            variant="outlined"
            :disabled="loading || !formValid || !selectedStatus"
            type="submit"
            :loading="loading"
          >
            {{ $t('COMMON.BUTTON.VALIDATE') }}
          </v-btn>
        </v-col>
        <v-col>
          <v-btn
            class="boom-button-back dialog-button"
            variant="outlined"
            @click="closeDialog()"
            :loading="loading"
          >
            {{ $t('COMMON.BUTTON.CANCEL') }}
          </v-btn>
        </v-col>
      </v-row>
    </v-form>
  </popup-modal>
</template>

<script>
import PopupModal from '@/components/modal/PopupModal.vue'
import { displayToast, handleResponseError, shouldShowTooltip } from '@/components/utils/globalFunction'
import repairFileService from '@/services/RepairFileService'
import RepairFileStatusDetailConstant from '@/constants/repairFileStatusDetailConstant'
import { auth as $store } from '@/store/AuthModule'
import RepairFileStatusMacroConstant from '@/constants/repairFileStatusMacroConstant'
import { VPhoneInput } from 'v-phone-input'
import RegionCodeService from '@/services/RegionCodeService'
import Validator from '@/components/utils/validator'
import DialogCloseButton from '@/components/modal/DialogCloseButton.vue'

export default {
  name: 'UpdateStatusFormModal',
  computed: {
    RepairFileStatusMacroConstant() {
      return RepairFileStatusMacroConstant
    }
  },
  components: { DialogCloseButton, VPhoneInput, PopupModal },

  data() {
    const date = new Date()
    date.setDate(date.getUTCDate())
    date.setHours(0, 0, 0, 0)

    return {
      formValid: false,

      currentRepairFile: null,
      loading: false,
      selectedStatus: null,
      availableStatus: [],
      realUpdatingDate: date,
      today: new Date(),

      emailAddress: null,

      clientPhoneNotificationType: 'CALL_NOTIFICATION', // Potential value: CALL_NOTIFICATION or SMS_NOTIFICATION
      clientTakeoverType: 'PICK_UP', // Potential value: PICK_UP or SEND_BY_STORE

      showValidDate: false,

      allCountries: [],
      selectedCountry: '',
      smsPhoneNumber: null,

      requiredRules: Validator.requiredRules,
      emailRules: Validator.emailRules,
      max255CharRules: Validator.max255CharRules,

      error: null
    }
  },
  methods: {
    shouldShowTooltip,
    formatDate() {
      const date = this.realUpdatingDate
      const day = date.getDate().toString().padStart(2, '0')
      const month = (date.getMonth() + 1).toString().padStart(2, '0')
      const year = date.getFullYear()
      return `${day}/${month}/${year}`
    },
    async show(opts = {}) {
      await this.getRegionCode()

      this.currentRepairFile = opts.currentRepairFile
      this.emailAddress = this.currentRepairFile.email
      this.smsPhoneNumber = this.currentRepairFile.phone_number

      try {
        const response = await repairFileService.getAvailableRepairFileStatusUpdate(
          this.currentRepairFile.technical_id
        )
        const available_detail_status_list = response.available_status_list

        if (
          available_detail_status_list.includes(
            RepairFileStatusDetailConstant.RECEPTION_AT_REPAIRER
          )
        ) {
          this.availableStatus.push({
            key: RepairFileStatusMacroConstant.RECEPTION_AT_REPAIRER,
            value: this.$t('REPAIR_FILE_GLOBAL_INFO.STATUS_DISPLAY.RECEPTION_AT_REPAIRER')
          })
        }

        if (
          available_detail_status_list.includes(
            RepairFileStatusDetailConstant.DIAGNOSTIC_MANUFACTURER_RECEIVED
          )
        ) {
          this.availableStatus.push({
            key: RepairFileStatusMacroConstant.DIAGNOSTIC_MANUFACTURER_RECEIVED,
            value: this.$t(
              'REPAIR_FILE_GLOBAL_INFO.STATUS_DISPLAY.DIAGNOSTIC_MANUFACTURER_RECEIVED'
            )
          })
        }

        if (available_detail_status_list.includes(RepairFileStatusDetailConstant.IN_REPARATION)) {
          this.availableStatus.push({
            key: RepairFileStatusMacroConstant.IN_REPARATION,
            value: this.$t('REPAIR_FILE_GLOBAL_INFO.STATUS_DISPLAY.IN_REPARATION')
          })
        }

        if (
          available_detail_status_list.includes(
            RepairFileStatusDetailConstant.SENT_BACK_BY_REPAIRER
          )
        ) {
          this.availableStatus.push({
            key: RepairFileStatusMacroConstant.SENT_BACK_BY_REPAIRER,
            value: this.$t('REPAIR_FILE_GLOBAL_INFO.STATUS_DISPLAY.SENT_BACK_BY_REPAIRER')
          })
        }

        if (
          available_detail_status_list.includes(RepairFileStatusDetailConstant.RECEIPT_AT_STORE)
        ) {
          this.availableStatus.push({
            key: RepairFileStatusMacroConstant.RECEIPT_AT_STORE,
            value: this.$t('REPAIR_FILE_GLOBAL_INFO.STATUS_DISPLAY.RECEIPT_AT_STORE')
          })
        }

        if (
          available_detail_status_list.includes(
            RepairFileStatusDetailConstant.SENT_AVAILABLE_TO_CUSTOMER
          )
        ) {
          this.availableStatus.push({
            key: RepairFileStatusMacroConstant.SENT_AVAILABLE_TO_CUSTOMER,
            value: this.$t('REPAIR_FILE_GLOBAL_INFO.STATUS_DISPLAY.SENT_AVAILABLE_TO_CUSTOMER')
          })
        }
      } catch (error) {
        handleResponseError(error)
      } finally {
        this.loading = false
      }
      this.$refs.popup.open()

      return new Promise((resolve, reject) => {
        this.resolvePromise = resolve
        this.rejectPromise = reject
      })
    },
    async saveAndClose() {
      try {
        let statusToSend = null
        if (this.selectedStatus === RepairFileStatusMacroConstant.RECEPTION_AT_REPAIRER) {
          statusToSend = RepairFileStatusDetailConstant.RECEPTION_AT_REPAIRER
        } else if (
          this.selectedStatus === RepairFileStatusMacroConstant.DIAGNOSTIC_MANUFACTURER_RECEIVED
        ) {
          statusToSend = RepairFileStatusDetailConstant.DIAGNOSTIC_MANUFACTURER_RECEIVED
        } else if (this.selectedStatus === RepairFileStatusMacroConstant.DIAGNOSTIC_MANUFACTURER_PROCESSED
          || this.selectedStatus === RepairFileStatusMacroConstant.ESTIMATE_SENT_TO_CUSTOMER
          || this.selectedStatus === RepairFileStatusMacroConstant.ESTIMATE_CUSTOMER_DECISION_SENT_TO_REPAIRER) {
          displayToast(this.$t('REPAIR_FILE_GLOBAL_INFO.ERROR.MANUAL_UPDATE_INVALID'), 'warning')
          return
        } else if (this.selectedStatus === RepairFileStatusMacroConstant.IN_REPARATION) {
          statusToSend = RepairFileStatusDetailConstant.IN_REPARATION
        } else if (this.selectedStatus === RepairFileStatusMacroConstant.SENT_BACK_BY_REPAIRER) {
          statusToSend = RepairFileStatusDetailConstant.SENT_BACK_BY_REPAIRER
        } else if (this.selectedStatus === RepairFileStatusMacroConstant.RECEIPT_AT_STORE) {
          statusToSend = RepairFileStatusDetailConstant.RECEIPT_AT_STORE
        } else if (
          this.selectedStatus === RepairFileStatusMacroConstant.SENT_AVAILABLE_TO_CUSTOMER
        ) {
          statusToSend = RepairFileStatusDetailConstant.SENT_AVAILABLE_TO_CUSTOMER
        } else {
          statusToSend = RepairFileStatusDetailConstant.FINISHED
        }

        if (statusToSend !== RepairFileStatusDetailConstant.RECEIPT_AT_STORE) {
          await repairFileService.updateRepairFileStatus(
            this.currentRepairFile.technical_id,
            this.realUpdatingDate,
            statusToSend,
            $store.state.currentCollaborator.technical_id
          )
        } else {
          await repairFileService.updateRepairFileStatusToReceiptAtStore(
            this.currentRepairFile.technical_id,
            this.realUpdatingDate,
            statusToSend,
            $store.state.currentCollaborator.technical_id,
            this.clientTakeoverType,
            this.emailAddress,
            this.clientPhoneNotificationType,
            this.smsPhoneNumber
          )
        }

        this.selectedStatus = null
        this.availableStatus = []
        this.resetDate()
        this.$refs.popup.close()
        this.resolvePromise(true)
      } catch (error) {
        handleResponseError(error)
      } finally {
        this.loading = false
      }
    },
    closeDialog() {
      this.selectedStatus = null
      this.availableStatus = []
      this.loading = false
      this.$refs.popup.close()
      this.resolvePromise(false)
      this.resetDate()
    },
    setError(key) {
      switch (key) {
        default:
          this.error = ''
      }
    },
    resetDate() {
      const date = new Date()
      date.setDate(date.getUTCDate())
      date.setHours(0, 0, 0, 0)
      this.realUpdatingDate = date
    },
    getMinDate() {
      const date = new Date(this.currentRepairFile.finalization_date.replace(' ', 'T'))
      date.setDate(date.getDate() - 1)
      return date
    },
    async getRegionCode() {
      this.loading = true
      try {
        const response = await RegionCodeService.getRegionCodeList()
        this.allCountries = this.getCountryCode(response.item_list)
      } catch (error) {
        handleResponseError(error)
      } finally {
        this.loading = false
      }
    },
    getCountryCode(zones) {
      return zones.map((item) => {
        return item.country_code
      })
    }
  }
}
</script>

<style scoped>
.title {
  font-size: 24px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
}

.content {
  display: flex;
  justify-content: center;
}

.dialog-button {
  min-width: 100px;
  width: 100%;
}

.sub-title {
  font-size: 18px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}

.date-picker {
  width: 16px;
  filter: invert(48%) sepia(0%) saturate(78%) hue-rotate(135deg) brightness(95%) contrast(83%);
}

.date-creation {
  display: flex;
  justify-items: center;
  color: #767676;
  padding: 4px;
  min-width: 125px;
}

:deep(.date-creation .v-input__control) {
  min-width: 125px;
}

.date-picker,
.date-creation {
  cursor: pointer;
}

.receipt-at-store {
  .title {
    font-size: 16px;
    color: #7d7d7d;
  }

  .phone-notification-title,
  .takeover-type-title {
    color: #72ae4b;
    justify-self: center;
  }

  .email-takeover-part {
    width: 340px;

    .custom-divider {
      padding-left: 0;
      padding-right: 0;
    }

    .custom-takeover-part {
      margin-left: -25px;
    }
  }
}
</style>
